import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { num, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { ProdStatusBadge } from "./Production";

export default function ProductionChecker() {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    const r = await api.get("/production-checker");
    setRows(r.data);
  }, []);
  useEffect(() => { load().catch((e) => console.error(e)); }, [load]);

  return (
    <div data-testid="page-production-checker" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Produksi</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Checker Produksi</h1>
        <p className="text-slate-500 text-sm mt-1">Validasi: paket (good + reject = target), bahan (diambil = terpakai + sisa + waste), waste pending. Klik baris bermasalah untuk detail.</p>
      </div>

      <Card>
        <table className="w-full text-sm" data-testid="table-prod-checker">
          <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
            <th className="py-2 px-4"></th><th>Produksi</th><th>Produk</th><th className="text-right">Target</th>
            <th className="text-right">Good</th><th className="text-right">Reject</th><th className="text-right">Selisih</th>
            <th>Paket</th><th>Bahan</th><th>Status</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const hasIssue = !r.pkg_ok || !r.mat_ok || r.pending_waste > 0;
              const open = expanded[r.production_id];
              return (
                <React.Fragment key={r.production_id}>
                  <tr className={`border-b ${hasIssue ? "bg-rose-50 cursor-pointer hover:bg-rose-100" : ""}`}
                    onClick={() => hasIssue && setExpanded({ ...expanded, [r.production_id]: !open })}
                    data-testid={`checker-row-${r.prod_no}`}>
                    <td className="py-2 px-4 w-8">{hasIssue ? (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : null}</td>
                    <td className="font-mono text-xs">{r.prod_no}</td>
                    <td>{r.product_name}</td>
                    <td className="text-right font-mono">{num(r.target_dus)}</td>
                    <td className="text-right font-mono text-emerald-700">{num(r.good_dus)}</td>
                    <td className="text-right font-mono text-rose-700">{num(r.reject_dus)}</td>
                    <td className={`text-right font-mono ${r.selisih_dus ? "text-rose-700 font-bold" : ""}`}>{num(r.selisih_dus)}</td>
                    <td>{r.pkg_ok ? <Badge className="bg-emerald-100 text-emerald-800 font-normal">OK</Badge> : <Badge className="bg-rose-100 text-rose-800 font-normal" data-testid={`checker-pkg-issue-${r.prod_no}`}>Ada Selisih</Badge>}</td>
                    <td>{r.mat_ok ? <Badge className="bg-emerald-100 text-emerald-800 font-normal">OK</Badge> : <Badge className="bg-rose-100 text-rose-800 font-normal" data-testid={`checker-mat-issue-${r.prod_no}`}>Ada Selisih</Badge>}
                      {r.pending_waste > 0 && <Badge className="bg-amber-100 text-amber-800 font-normal ml-1">{r.pending_waste} waste pending</Badge>}
                    </td>
                    <td><ProdStatusBadge status={r.status} /></td>
                  </tr>
                  {open && (
                    <tr className="bg-rose-50/60 border-b" data-testid={`checker-detail-${r.prod_no}`}>
                      <td></td>
                      <td colSpan={9} className="py-2 text-sm">
                        {r.pkg_issues.map((x, i) => (
                          <div key={i} className="flex items-center gap-2 text-rose-800 py-0.5">
                            <AlertTriangle className="h-3.5 w-3.5" /> PAKET #{x.package} ({x.operator}): {x.detail}
                          </div>
                        ))}
                        {r.mat_issues.map((x, i) => (
                          <div key={i} className="flex items-center gap-2 text-rose-800 py-0.5">
                            <AlertTriangle className="h-3.5 w-3.5" /> BAHAN {x.sku} · {x.name}: {x.detail}
                          </div>
                        ))}
                        {r.pending_waste > 0 && <div className="flex items-center gap-2 text-amber-800 py-0.5"><AlertTriangle className="h-3.5 w-3.5" /> {r.pending_waste} waste menunggu approval Owner/Manager</div>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-400">Belum ada produksi</td></tr>}
          </tbody>
        </table>
      </Card>

      {rows.length > 0 && rows.every((r) => r.pkg_ok && r.mat_ok && r.pending_waste === 0) && (
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-5 flex items-center gap-3" data-testid="checker-all-ok">
          <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          <div className="text-emerald-900 font-medium">Semua produksi seimbang — tidak ada selisih paket maupun bahan.</div>
        </div>
      )}
    </div>
  );
}
